import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { RechargeAdjustmentList } from '../api/types';
import { formatDateTime, formatMinutes, POOL_LABEL } from '../lib/format';
import { Badge, Button, Card, EmptyState, ErrorNote, Spinner } from '../components/ui';

const PAGE_SIZE = 50;

// Every correction ever made to prepaid time, newest first.
//
// This is a ledger, not a dashboard: nothing here is editable, nothing is
// hidden, and rows are never collapsed or summarised away. An owner opens it to
// answer "who gave away four hours last week, and did they say why?", and a
// ledger that quietly omits the boring rows cannot answer that.
export function RechargeAuditPage() {
  const [data, setData] = useState<RechargeAdjustmentList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.recharges.adjustments({
        limit: PAGE_SIZE,
        offset,
      });
      setData(result);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <Spinner label="Loading the audit trail…" />;
  if (error) return <ErrorNote>{error}</ErrorNote>;

  const rows = data?.adjustments ?? [];
  const shownTo = offset + rows.length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold">Recharge edits</h1>
        <p className="text-sm text-slate-500">
          Every change an admin has made to a customer's prepaid time. This
          record cannot be edited or deleted — a mistake is corrected by making
          the opposite adjustment, and both stay on the page.
        </p>
      </div>

      {/* Net, not just the two halves: an owner scanning this wants to know
          whether the cafe is giving time away faster than it claws it back. */}
      <Card className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Edits</p>
          <p className="text-xl font-semibold">{data?.total_count ?? 0}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Time added</p>
          <p className="text-xl font-semibold text-emerald-300">
            +{formatMinutes(data?.minutes_added ?? 0)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Time taken back
          </p>
          <p className="text-xl font-semibold text-rose-300">
            {formatMinutes(Math.abs(data?.minutes_removed ?? 0))}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">Net</p>
          <p className="text-xl font-semibold">
            {(data?.minutes_added ?? 0) + (data?.minutes_removed ?? 0) >= 0 ? '+' : '−'}
            {formatMinutes(
              Math.abs((data?.minutes_added ?? 0) + (data?.minutes_removed ?? 0)),
            )}
          </p>
        </div>
      </Card>

      {rows.length === 0 ? (
        <EmptyState title="No recharge edits yet.">
          Adjustments made from a customer's Prepaid time list show up here.
        </EmptyState>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {rows.map((row) => (
            <li key={row.adjustment_id} className="space-y-2 px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Link
                    to={`/customers/${row.customer_id}`}
                    className="text-sm font-medium text-sky-300 hover:underline"
                  >
                    {row.customer_name}
                  </Link>
                  <p className="text-xs text-slate-500">
                    {row.customer_phone} · {row.pack_label ?? 'Recharge'} ·{' '}
                    {POOL_LABEL[row.pool]}
                  </p>
                </div>
                <div className="text-right">
                  <Badge tone={row.delta_minutes > 0 ? 'green' : 'red'}>
                    {row.delta_minutes > 0 ? '+' : '−'}
                    {formatMinutes(Math.abs(row.delta_minutes))}
                  </Badge>
                  {/* The before/after pair is the point of the row: the delta
                      alone does not say whether a balance was plausible. */}
                  <p className="mt-1 text-xs text-slate-500">
                    {formatMinutes(row.minutes_before)} →{' '}
                    {formatMinutes(row.minutes_after)}
                  </p>
                </div>
              </div>

              <p className="text-sm text-slate-300">{row.reason}</p>

              <p className="text-xs text-slate-500">
                {row.adjusted_by_name ?? 'Unknown staff'} ·{' '}
                {formatDateTime(row.created_at)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {(offset > 0 || (data?.total_count ?? 0) > shownTo) && (
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Newer
          </Button>
          <span className="text-xs text-slate-500">
            {offset + 1}–{shownTo} of {data?.total_count ?? 0}
          </span>
          <Button
            variant="secondary"
            disabled={shownTo >= (data?.total_count ?? 0) || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Older
          </Button>
        </div>
      )}
    </div>
  );
}
